/**
 * WebP 支持检测与图片路径转换工具
 * 
 * 用法：
 *   import { supportsWebP, webpUrl } from '@/utils/webp'
 *   const src = webpUrl(require('@/assets/images/photo.jpg'))
 *   // → 如果浏览器支持 WebP，返回 photo.webp，否则返回 photo.jpg
 */

// 缓存 WebP 检测结果（只检测一次）
let webpSupport = null
let webpCheckPromise = null

/**
 * 检测浏览器是否支持 WebP（有损格式）
 * @returns {Promise<boolean>}
 */
export function checkWebpSupport() {
  if (webpCheckPromise) return webpCheckPromise

  webpCheckPromise = new Promise((resolve) => {
    // 服务端渲染或无 window 环境
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      webpSupport = false
      resolve(false)
      return
    }

    // 使用 canvas 检测法（最可靠）
    const canvas = document.createElement('canvas')
    if (!canvas || !canvas.getContext) {
      // 降级方案：通过特征检测
      const elem = document.createElement('canvas')
      webpSupport = elem.toDataURL('image/webp').indexOf('data:image/webp') === 0
      resolve(webpSupport)
      return
    }

    // 尝试绘制 2x2 的 WebP 图片数据
    const webpData = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoCAAEAAQAcJaQAA3AA/v3AgAA='
    const img = new Image()
    
    img.onload = () => {
      webpSupport = img.width > 0 && img.height > 0
      resolve(webpSupport)
    }
    img.onerror = () => {
      webpSupport = false
      resolve(false)
    }
    img.src = webpData
  })

  return webpCheckPromise
}

/**
 * 同步获取 WebP 支持状态（需先调用 checkWebpSupport）
 */
export function supportsWebP() {
  return webpSupport === true
}

/**
 * 将图片路径转换为 WebP（如果浏览器支持）
 * @param {string} src - 原始图片路径（如 /img/photo.jpg）
 * @returns {string} WebP 路径或原路径
 * 
 * 示例：
 *   webpUrl('/assets/images/photo.jpg')  → '/assets/images/photo.webp'
 *   webpUrl('/assets/images/photo.png')  → '/assets/images/photo.webp'
 *   webpUrl('/assets/images/icon.svg')   → '/assets/images/icon.svg' (不变)
 */
export function webpUrl(src) {
  if (!src || !supportsWebP()) return src

  // 只转换 jpg/jpeg/png
  const ext = src.split('.').pop().toLowerCase()
  if (['jpg', 'jpeg', 'png'].includes(ext)) {
    return src.replace(/\.(jpg|jpeg|png)$/i, '.webp')
  }
  return src
}

/**
 * 初始化 WebP 支持检测（在 main.js 中调用）
 * 返回 Promise，解析后即可使用 webpUrl
 */
export function initWebp() {
  return checkWebpSupport()
}
